import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Quote, CheckCircle2 } from 'lucide-react';
import { getCaseStudy } from '../components/caseStudiesData';
import { CtaBand } from '../components/page/CtaBand';
import { Badge } from '../components/ui/Badge';
import { Reveal } from '../components/ui/Reveal';
export const CaseStudyDetail = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const study = slug ? getCaseStudy(slug) : undefined;
  useEffect(() => {
    if (!study) {
      navigate('/case-studies');
    }
  }, [study, navigate]);
  if (!study) return null;
  return <div className="pt-24 pb-12" data-id="element-2488" data-mp="2n8o9amzx86s7">
      {/* Hero Section */}
      <section className="relative pt-12 pb-16 lg:pt-20 lg:pb-24 overflow-hidden border-b border-gray-100" data-id="element-2489" data-mp="2i8a91r4ka14q">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-zano-lavender/30 rounded-full blur-3xl -z-10" data-id="element-2490" data-mp="2w7t4bjc9xhzm" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2491" data-mp="17zefphvkjbob">
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-zano-pink transition-colors mb-8" data-id="element-2492" data-mp="1817k0qipljt5">
            <ArrowLeft size={16} data-id="element-2493" data-mp="1hw99jngwcnw9" /> Back to Case Studies
          </Link>

          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start" data-id="element-2494" data-mp="1mxottdb92qdl">
            <div className="lg:w-3/5" data-id="element-2495" data-mp="1qys83vdngecp">
              <div className="flex flex-wrap gap-2 mb-6" data-id="element-2496" data-mp="1qxz06r2yfisz">
                {study.tags.map(tag => <Badge key={tag} className="bg-blue-50 text-blue-700 border border-blue-200" data-id="element-2497" data-mp="1vvirosonxs2v">
                    {tag}
                  </Badge>)}
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6 leading-tight" data-id="element-2498" data-mp="2amrmrhbc45ne">
                {study.title}
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8" data-id="element-2499" data-mp="2elqo6rxuxka3">
                {study.subtitle}
              </p>
            </div>

            <div className="lg:w-2/5 w-full" data-id="element-2500" data-mp="akiblqcnxx2v">
              <div className="bg-gray-50 rounded-3xl p-8 border border-gray-100" data-id="element-2501" data-mp="altezp2752tg">
                <div className={`w-full h-32 rounded-xl flex items-center justify-center mb-8 ${study.logoBg}`} data-id="element-2502" data-mp="oc6lro3cqbh7">
                  <img src={study.logo} alt={`${study.brand} logo`} className="max-h-16 max-w-[200px] object-contain" data-id="element-2503" data-mp="y9koym09l3rk" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2504" data-mp="17ar8z524gwpk">
                  Key Results
                </h3>
                <ul className="space-y-3" data-id="element-2505" data-mp="139paiqg14oon">
                  {study.results.map((result, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-2506" data-mp="392no0oni0op3" data-mpi={i}>
                      <CheckCircle2 size={20} className="text-zano-pink shrink-0 mt-0.5" data-id="element-2507" data-mp="352dgh7k25q84" />
                      <span data-id="element-2508" data-mp="2ywe14evhulcr">{result}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 lg:py-24" data-id="element-2509" data-mp="2q3j4g9k3dxyl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2510" data-mp="2b7lb0z1jz7q2">
          <div className="flex flex-col lg:flex-row gap-16" data-id="element-2511" data-mp="2ba7eamhev12h">
            {/* Main Content */}
            <div className="lg:w-2/3 prose prose-lg prose-pink max-w-none" data-id="element-2512" data-mp="2g53alytokale">
              <Reveal data-id="element-2513" data-mp="228ziqc6a4ec3">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3" data-id="element-2514" data-mp="229hl7qjjbosg">
                  <span className="text-yellow-500" data-id="element-2515" data-mp="278lxiq1ra2n0">⚡</span> The Challenge
                </h2>
                {study.challenge.map((p, i) => <p key={i} className="text-gray-700 leading-relaxed mb-4" data-id="element-2516" data-mp="5fejdjxvpr2p" data-mpi={i}>
                    {p}
                  </p>)}
              </Reveal>

              <Reveal delay={0.1} data-id="element-2517" data-mp="21fx1ovig9jse">
                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3" data-id="element-2518" data-mp="1wiwuuids0d08">
                  <span className="text-blue-500" data-id="element-2519" data-mp="1rjbvw7ylw4ni">🚀</span> The Solution: Using
                  Zano
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-2520" data-mp="1nhqeqaf1h47i">
                  {study.solution.intro}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-8" data-id="element-2521" data-mp="1ilhz6vwxj8zt">
                  {study.solution.bullets.map((bullet, i) => <li key={i} data-id="element-2522" data-mp="18su7gfkt3v7y" data-mpi={i}>{bullet}</li>)}
                </ul>
              </Reveal>

              <Reveal delay={0.2} data-id="element-2523" data-mp="13vk92affw97z">
                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3" data-id="element-2524" data-mp="13wc2belkduv3">
                  <span className="text-purple-500" data-id="element-2525" data-mp="zv0bx6uv95lu">🔍</span> How It Worked
                </h2>
                <ol className="list-decimal pl-6 space-y-3 text-gray-700 mb-8" data-id="element-2526" data-mp="uumc2c3f51yw">
                  {study.howItWorked.map((step, i) => <li key={i} className="pl-2" data-id="element-2527" data-mp="3bg0vza840yxb" data-mpi={i}>
                      {step}
                    </li>)}
                </ol>
              </Reveal>

              <Reveal delay={0.3} data-id="element-2528" data-mp="3ffrrtschu58p">
                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3" data-id="element-2529" data-mp="3khh75k6r152l">
                  <span className="text-emerald-500" data-id="element-2530" data-mp="2nr869vi2znb5">📈</span> The Results
                </h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-8" data-id="element-2531" data-mp="2rrs3t06kgsxt">
                  {study.resultsDetailed.map((result, i) => <li key={i} data-id="element-2532" data-mp="2wptn9m7wmu9t" data-mpi={i}>{result}</li>)}
                </ul>
              </Reveal>

              {study.keyInsight && <Reveal delay={0.4} data-id="element-2533" data-mp="242z3kriwvyxe">
                  <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3" data-id="element-2534" data-mp="241xs4lxzhaf5">
                    <span className="text-amber-500" data-id="element-2535" data-mp="290hegnghoo81">💡</span> Key Insight
                  </h2>
                  <p className="text-gray-700 leading-relaxed bg-amber-50 p-6 rounded-2xl border border-amber-100" data-id="element-2536" data-mp="3cberar1yay5w">
                    {study.keyInsight}
                  </p>
                </Reveal>}
            </div>

            {/* Sidebar */}
            <div className="lg:w-1/3 space-y-8" data-id="element-2537" data-mp="3h3oew0flfm50">
              <Reveal delay={0.2} data-id="element-2538" data-mp="3h3y4hmwq7ra6">
                <div className="bg-zano-blush/30 rounded-3xl p-8 border border-zano-pink/20" data-id="element-2539" data-mp="2tdu2fjz6rzla">
                  <Quote size={32} className="text-zano-pink mb-6" data-id="element-2540" data-mp="2tfolstut7jn9" />
                  <p className="text-xl text-gray-900 font-medium italic leading-relaxed mb-6" data-id="element-2541" data-mp="2ogbtvup2zmdt">
                    "{study.quote.text}"
                  </p>
                  <div data-id="element-2542" data-mp="2xexerwi0zdhr">
                    <p className="font-bold text-gray-900" data-id="element-2543" data-mp="2xenr4fkgdif8">
                      {study.quote.author}
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-2544" data-mp="hqdyhxxny96i">{study.quote.role}</p>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.3} data-id="element-2545" data-mp="l5yzj69pr899">
                <div className="bg-gray-50 rounded-3xl p-8 border border-gray-100" data-id="element-2546" data-mp="l4nmefudoo5h">
                  <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2547" data-mp="p8lkqobvpuxh">
                    About {study.brand}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6" data-id="element-2548" data-mp="ba5bapmmtqbn">
                    {study.about.text}
                  </p>
                  <a href={study.about.website} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-zano-pink hover:text-zano-pinkDark transition-colors" data-id="element-2549" data-mp="13w6omkgkvy6g">
                    Visit Website &rarr;
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <CtaBand title="Ready to see similar results?" subtitle="Join the top agencies and brands using Zano to scale their influencer campaigns." primaryCta={{
      label: 'Start Free Trial',
      to: '/pricing'
    }} data-id="element-2550" data-mp="1mu9dmz94lv49" />
    </div>;
};